/**
 * Convert RFI front-end data to back-end data
 */

import { RequestPropertyIds } from '../../../data/BackendEntityConstants';
import { RFIRequestForm, RFIRequestInformation, RFIType } from '../../../interfaces/RFIRequestForm';
import { Document, Property, RFXRequest, RFXRevisionedId } from '../../../interfaces/RFXBackendModel';
import { convertCoManagerEntriesToProperty } from '../../CoManagerUtils';
import { createProperty } from '../../RFXUtils';
import {
  convertCategories,
  convertRegions,
  convertRFXRequestDocumentsToBackendModel,
  convertSites,
  convertSupplierEntries,
  convertTrackingInfoToProperties,
  generateRevisionedId
} from '../common/backendConverter';
import { generateRFIRequestCommon } from './helper';

/**
 * Convert general information to back-end properties
 * @param generalInfo general information from UI
 * @returns converted properties that can be used for back-end
 */
function convertGeneralInfo(generalInformation: RFIRequestInformation): Property[] {
  const properties: Property[] = [];
  const { rfiType, regions, sites, categories, project, approvalLink, subCategories } = generalInformation;
  properties.push(createProperty(RequestPropertyIds.CorrespondenceType, rfiType));
  properties.push(convertRegions(regions));
  properties.push(convertSites(sites));
  properties.push(convertCategories(categories));
  // Adding SubCategories:
  if (subCategories) {
    properties.push(createProperty(RequestPropertyIds.SubCategories, subCategories.join(',')));
  }
  properties.push(createProperty(RequestPropertyIds.Project, project));
  if (rfiType === RFIType.MassCommunication && approvalLink !== undefined) {
    properties.push(createProperty(RequestPropertyIds.ApprovalLink, approvalLink));
  }
  return properties;
}

/**
 * Convert front-end RFIRequestForm to back-end RFXRequest
 *
 * @param rfxRequestForm front-end RFIRequestForm
 * @returns converted RFXRequest
 */
export function convertRFIRequestFormToRFXRequest(rfxRequestForm: RFIRequestForm): RFXRequest {
  const { rfxRequestVersion, generalInformation, correspondence, state } = rfxRequestForm;
  const { rfxName, businessId, fullRfxId, beginDate, beginTime, dueDate, dueTime, overview, processDetails } =
    generalInformation;
  const { subject, body, bodyText } = correspondence;
  const rfxRequestRevisionedId: RFXRevisionedId = generateRevisionedId(fullRfxId, 1);

  const buyer = rfxRequestForm.generalInformation.buyer;

  const name = rfxName.trim();
  if (name === '') {
    throw new Error('RFX name is required.');
  }

  if (businessId === null) {
    throw new Error('Business ID is required.');
  }

  if (beginDate === '') {
    throw new Error('Begin date is required.');
  }
  if (beginTime === '') {
    throw new Error('Begin time is required.');
  }
  const beginAt = Date.parse(`${beginDate}T${beginTime}`);

  if (dueDate === '') {
    throw new Error('Required answer date is required.');
  }
  if (dueTime === '') {
    throw new Error('Required answer time is required.');
  }
  const dueAt = Date.parse(`${dueDate}T${dueTime}`);
  if (dueAt < beginAt) {
    throw new Error('Required answer date must be later than begin date.');
  }

  const properties: Property[] = [];
  properties.push(...convertGeneralInfo(rfxRequestForm.generalInformation));

  properties.push(createProperty(RequestPropertyIds.Subject, subject));
  properties.push(createProperty(RequestPropertyIds.Body, body));
  properties.push(createProperty(RequestPropertyIds.BodyText, bodyText));

  properties.push(...convertTrackingInfoToProperties(rfxRequestForm.trackingInformation));

  properties.push(convertCoManagerEntriesToProperty(rfxRequestForm['co-managers'].coManagerEntries));

  const documents: Document[] = convertRFXRequestDocumentsToBackendModel(rfxRequestForm.documents);

  const rfxRequestBackendModel: RFXRequest = {
    ...generateRFIRequestCommon(),
    name,
    rfxRequestRevisionedId,
    rfxRequestVersion,
    businessId: { value: businessId },
    beginAt,
    dueAt,
    buyer: { value: buyer },
    generalInformation: { overview, processDetail: processDetails },
    suppliers: convertSupplierEntries(rfxRequestForm.suppliers.supplierEntries, businessId, businessId),
    documents,
    lifeCycle: {
      lifeCycleGraphId: 'DEFAULT_AWS_RFI_REQUEST',
      lifeCycleState: { name: state, version: 1 },
      lifeCycleVersion: 1
    },
    properties
  };

  return rfxRequestBackendModel;
}
